import { createContext, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";

const ErrorFilterContext = createContext(null);

export const useErrorFilter = () => useContext(ErrorFilterContext);

const DOM_ERROR_PATTERNS = [
  "removeChild",
  "insertBefore",
  "The node to be removed is not a child",
  "The node before which the new node is to be inserted",
  "The object can not be found here.",
];

// Błędy zewnętrznych bibliotek/środowisk, które powinny być zignorowane
const EXTERNAL_ERROR_PATTERNS = [
  "Java object is gone", // Facebook WebView błąd na Androidzie
  "postMessage", // Błędy komunikacji z natywnym kodem (Facebook, Instagram WebView)
  "ResizeObserver loop limit exceeded", // Niegroźny błąd przeglądarki
  "ResizeObserver loop completed with undelivered notifications",
];

export function ErrorFilterProvider({ children }: any) {
  // MutationObserver został usunięty - powodował nieskończoną rekurencję
  // poprzez console.log, który modyfikował DOM i wyzwalał kolejne mutacje

  useEffect(() => {
    const handler = (e: any) => {
      const msg = e?.message || e?.reason?.message || "";

      // Sprawdź czy to błąd zewnętrzny (Facebook WebView, ResizeObserver itp.)
      const isExternalError = EXTERNAL_ERROR_PATTERNS.some((pattern) =>
        msg.includes(pattern)
      );

      if (isExternalError) {
        // Całkowicie ignoruj błędy zewnętrzne - nie loguj ich nawet do Sentry
        e.preventDefault?.();
        e.stopPropagation?.();
        e.stopImmediatePropagation?.();
        return false;
      }

      // Sprawdź czy to błąd DOM
      const isDomError = DOM_ERROR_PATTERNS.some((pattern) =>
        msg.includes(pattern)
      );

      if (!isDomError) return;

      // --- 1. Zatrzymujemy oryginalny błąd ---
      e.preventDefault?.();

      // --- 2. Log do Sentry, ale jako BEZPIECZNY błąd ---
      Sentry.captureException(new Error(`DOMErrorBlocked: ${msg}`));

      return false;
    };

    window.addEventListener("error", handler, true); // Użyj capture phase
    window.addEventListener("unhandledrejection", handler, true);

    return () => {
      window.removeEventListener("error", handler, true);
      window.removeEventListener("unhandledrejection", handler, true);
    };
  }, []);

  return (
    <ErrorFilterContext.Provider value={null}>
      {children}
    </ErrorFilterContext.Provider>
  );
}
